import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { bungie, img } from '../../lib/bungie';
import { items, table } from '../../lib/manifest';
import { pgcr } from '../activity';
import { useHub } from '../context';
import { CLASSES, type Player } from '../hub';
import { Asking, Chip, SmallButton } from '../ui';
import '../tracker.css';

/**
 * D2 Tracker (port of the app's TrackerPage): the Guardian's recent Crucible matches by playlist, who played in
 * each, the weekly private-match points and the full match report. Public data: works for any looked-up Guardian.
 */

const MODES = [
  { key: 'comp', name: 'Competitive', modes: [69] },
  { key: 'all', name: 'All + private', modes: [5, 32] },
  { key: 'private', name: 'Private matches', modes: [32] },
  { key: 'crucible', name: 'All Crucible', modes: [5] },
  { key: 'trials', name: 'Trials', modes: [84] },
  { key: 'ib', name: 'Iron Banner', modes: [19] },
] as const;
type Mode = (typeof MODES)[number];
const TEAM_COLORS = ['#3D7BD9', '#D9534F', '#4FA36B', '#C9A13B'];
// as the app: Alpha is always blue and Bravo red, whichever Bungie lists first
const TEAM_BY_NAME: Record<string, string> = { Alpha: '#3D7BD9', Bravo: '#D9534F' };

interface MatchSummary {
  period: Date; instanceId: string; mapHash: number; modeHash: number; priv: boolean; cls: string; kills: number; deaths: number; assists: number;
  kd: number; eff: number; standing: string; team: string; score: number; seconds: number; map: string; mode: string;
}
interface PgcrPlayer { name: string; short: string; memberId: string; team: string; kills: number; deaths: number; score: number }

const val = (v: any, k: string): number => v?.[k]?.basic?.value ?? 0;
const shortName = (u: any): string => u?.bungieGlobalDisplayName || u?.displayName || '';
const fullName = (u: any): string => (u?.bungieGlobalDisplayName ? `${u.bungieGlobalDisplayName}#${String(u.bungieGlobalDisplayNameCode ?? 0).padStart(4, '0')}` : u?.displayName ?? '');

async function matchHistory(who: Player, modes: readonly number[], count: number): Promise<MatchSummary[]> {
  const [chars, acts] = await Promise.all([
    bungie<any>(`/Destiny2/${who.membershipType}/Profile/${who.membershipId}/?components=200`).then((r) => r.characters?.data ?? {}),
    table<any>('DestinyActivityDefinition'),
  ]);
  const found = new Map<string, MatchSummary>();
  await Promise.all(Object.entries<any>(chars).flatMap(([cid, c]) => modes.map(async (mode) => {
    let hist: any;
    try { hist = await bungie<any>(`/Destiny2/${who.membershipType}/Account/${who.membershipId}/Character/${cid}/Stats/Activities/?mode=${mode}&count=${count}&page=0`); }
    catch (e: any) { if (/Privacy/i.test(String(e?.message))) throw e; return; }
    for (const a of hist.activities ?? []) {
      const v = a.values, d = a.activityDetails;
      const priv = !!d?.isPrivate;
      found.set(d.instanceId, {
        period: new Date(a.period), instanceId: d.instanceId, mapHash: d.referenceId, modeHash: d.directorActivityHash, priv, cls: CLASSES[c.classType] ?? '',
        kills: val(v, 'kills'), deaths: val(v, 'deaths'), assists: val(v, 'assists'), kd: val(v, 'killsDeathsRatio'), eff: val(v, 'efficiency'),
        standing: v?.standing?.basic?.displayValue ?? '', team: String(v?.team?.basic?.value ?? ''), score: val(v, 'teamScore'), seconds: val(v, 'timePlayedSeconds'),
        map: acts[d.referenceId]?.displayProperties?.name || 'Unknown map', mode: acts[d.directorActivityHash]?.displayProperties?.name || (priv ? 'Private match' : 'Crucible'),
      });
    }
  })));
  return [...found.values()].sort((a, b) => b.period.getTime() - a.period.getTime()).slice(0, count);
}

const playersOf = (p: any): PgcrPlayer[] => (p?.entries ?? []).map((e: any) => {
  const u = e.player?.destinyUserInfo, v = e.values;
  return { name: fullName(u), short: shortName(u), memberId: u?.membershipId ?? '', team: String(v?.team?.basic?.value ?? ''), kills: val(v, 'kills'), deaths: val(v, 'deaths'), score: val(v, 'score') };
});
const matchPlayers = new Map<string, PgcrPlayer[]>();

/** Destiny's weekly reset: Tuesday 17:00 UTC. */
function weekRange(back: number) {
  const now = new Date();
  const days = (now.getUTCDay() - 2 + 7) % 7;
  let start = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - days, 17);
  if (start > now.getTime()) start -= 7 * 86400e3;
  start -= 7 * 86400e3 * back;
  return { start: new Date(start), end: new Date(start + 7 * 86400e3) };
}

const fmt = (d: Date, o: Intl.DateTimeFormatOptions) => d.toLocaleString(undefined, o);
const DAY_TIME: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' };
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

let lastMode: Mode = MODES[0];

export default function TrackerTab() {
  const h = useHub();
  const who = h.hub!.player;
  // ?mode=private: open on that mode (Activity > Hours' custom games tile links here, as in the app)
  const [params] = useSearchParams();
  const [mode, setMode] = useState<Mode>(() => MODES.find((m) => m.key === params.get('mode')) ?? lastMode);
  const [view, setView] = useState<'matches' | 'totals'>('matches');
  const [report, setReport] = useState<string>();
  const [stamp, setStamp] = useState(0);
  useEffect(() => { lastMode = mode; }, [mode]);
  if (report) return <Report id={report} me={who.membershipId} back={() => setReport(undefined)} />;
  const pickMode = (m: Mode) => { setMode(m); if (m.key !== 'private') setView('matches'); };
  return (
    <div className="t-page">
      <div className="t-top">
        <div className="g-chips">{MODES.map((m) => <Chip key={m.key} on={mode.key === m.key} onClick={() => pickMode(m)}>{m.name}</Chip>)}</div>
        <SmallButton onClick={() => setStamp((s) => s + 1)}>Refresh</SmallButton>
      </div>
      {mode.key === 'private' && (
        <div className="g-chips">
          <Chip on={view === 'matches'} onClick={() => setView('matches')}>Matches</Chip>
          <Chip on={view === 'totals'} onClick={() => setView('totals')}>Weekly totals</Chip>
        </div>
      )}
      {view === 'totals' && mode.key === 'private' ? <Totals key={stamp} who={who} /> : <Matches key={`${mode.key}${stamp}`} who={who} mode={mode} open={setReport} />}
    </div>
  );
}

function Matches({ who, mode, open }: { who: Player; mode: Mode; open: (id: string) => void }) {
  const [count, setCount] = useState(30);
  const [list, setList] = useState<MatchSummary[]>();
  const [error, setError] = useState('');
  const [time, setTime] = useState<Date>();
  const [, bump] = useState(0);
  useEffect(() => {
    let live = true;
    setError('');
    matchHistory(who, mode.modes, count).then((l) => {
      if (!live) return;
      setList(l);
      setTime(new Date());
      // who played in each match, a few at a time
      const todo = l.filter((m) => !matchPlayers.has(m.instanceId));
      let next = 0;
      const worker = async () => {
        while (live && next < todo.length) {
          const m = todo[next++];
          try { matchPlayers.set(m.instanceId, playersOf(await pgcr(m.instanceId))); bump((x) => x + 1); } catch { /* skipped */ }
        }
      };
      for (let i = 0; i < 6; i++) void worker();
    }).catch((e) => {
      if (!live) return;
      const msg = String(e?.message ?? e);
      setError(/Privacy/i.test(msg) ? 'This player hides their activity history. On bungie.net go to Settings > Privacy and allow others to see your Destiny game activity.' : msg);
    });
    return () => { live = false; };
  }, [who, mode, count]);

  if (error) return <Asking text={`Could not update: ${error}`} />;
  if (!list) return <Asking text={`Asking Bungie for ${mode.name.toLowerCase()}...`} />;
  const wins = list.filter((m) => m.standing === 'Victory').length;
  const decided = list.filter((m) => m.standing === 'Victory' || m.standing === 'Defeat').length;
  const k = list.reduce((s, m) => s + m.kills, 0), d = list.reduce((s, m) => s + m.deaths, 0), a = list.reduce((s, m) => s + m.assists, 0);
  return (
    <>
      <p className="t-status">{mode.name}   |   {list.length} matches ({list.filter((m) => m.priv).length} private)   |   updated {time?.toLocaleTimeString()}</p>
      <div className="t-cards">
        <div className="t-card"><span>Matches</span><b>{list.length}</b></div>
        <div className="t-card"><span>Win rate</span><b>{decided ? `${Math.round((100 * wins) / decided)}%  (${wins}-${decided - wins})` : '--'}</b></div>
        <div className="t-card"><span>K/D</span><b>{list.length ? (k / Math.max(1, d)).toFixed(2) : '--'}</b></div>
        <div className="t-card"><span>Efficiency</span><b>{list.length ? ((k + a) / Math.max(1, d)).toFixed(2) : '--'}</b></div>
      </div>
      {list.length === 0 && <p className="t-empty">No {mode.name.toLowerCase()} found in this player's recent history.</p>}
      {list.map((m) => <MatchRow key={m.instanceId} m={m} me={who.membershipId} onClick={() => open(m.instanceId)} />)}
      {list.length >= count && count < 250 && <div className="t-more"><SmallButton onClick={() => setCount(Math.min(250, count + 30))}>Show older matches</SmallButton></div>}
    </>
  );
}

function namesLine(m: MatchSummary, me: string) {
  const players = matchPlayers.get(m.instanceId);
  if (!players) return 'Loading players...';
  const mine = players.find((p) => p.memberId === me);
  const others = players.filter((p) => p.memberId !== me);
  if (mine && new Set(players.map((p) => p.team)).size > 1) {
    const w = others.filter((p) => p.team === mine.team).map((p) => p.short), vs = others.filter((p) => p.team !== mine.team).map((p) => p.short);
    return `With: ${w.length ? w.join(', ') : 'solo'}     Vs: ${vs.length ? vs.join(', ') : '-'}`;
  }
  return `Players: ${others.map((p) => p.short).join(', ')}`;
}

function MatchRow({ m, me, onClick }: { m: MatchSummary; me: string; onClick: () => void }) {
  const result = m.standing === 'Victory' ? 'VICTORY' : m.standing === 'Defeat' ? 'DEFEAT' : m.priv ? 'PRIVATE' : '--';
  const tone = m.standing === 'Victory' ? 'win' : m.standing === 'Defeat' ? 'loss' : 'none';
  return (
    <button type="button" className={`t-match ${tone}`} onClick={onClick} title="K / D / A, K/D, efficiency, time played. Click for the full match report.">
      <span className="grid">
        <b className="res">{result}</b>
        <span className="where"><b>{m.map}</b><span>{m.mode}   {fmt(m.period, DAY_TIME)}   {m.cls}</span></span>
        <span className="r">{m.kills} / {m.deaths} / {m.assists}</span>
        <b className={`r ${m.kd >= 1 ? 'good' : ''}`}>{m.kd.toFixed(2)}</b>
        <span className="r">{m.eff.toFixed(2)}</span>
        <span className="r dim">{mmss(m.seconds)}</span>
      </span>
      <span className="names">{namesLine(m, me)}</span>
    </button>
  );
}

// ---------------- weekly totals (private matches) ----------------

interface TotalsRow { name: string; short: string; memberId: string; points: number; kills: number; deaths: number; firsts: number; seconds: number; matches: number }

function Totals({ who }: { who: Player }) {
  const [back, setBack] = useState(0);
  const [progress, setProgress] = useState('Finding private matches...');
  const [rows, setRows] = useState<{ rows: TotalsRow[]; matches: number }>();
  const [error, setError] = useState('');
  const range = useMemo(() => weekRange(back), [back]);
  useEffect(() => {
    let live = true;
    setRows(undefined);
    setError('');
    setProgress('Finding private matches...');
    (async () => {
      const all = await matchHistory(who, [32], 250);
      const week = all.filter((m) => m.period >= range.start && m.period < range.end);
      const board = new Map<string, TotalsRow>();
      let i = 0;
      for (const m of week) {
        if (!live) return;
        setProgress(`Reading match ${++i} of ${week.length}...`);
        let players: PgcrPlayer[];
        try { players = playersOf(await pgcr(m.instanceId)); } catch { continue; }
        matchPlayers.set(m.instanceId, players);
        const kills = [...new Set(players.map((p) => p.kills))].sort((a, b) => b - a);
        for (const p of players) {
          let r = board.get(p.memberId);
          if (!r) board.set(p.memberId, (r = { name: p.name, short: p.short, memberId: p.memberId, points: 0, kills: 0, deaths: 0, firsts: 0, seconds: 0, matches: 0 }));
          r.matches++; r.kills += p.kills; r.deaths += p.deaths;
          if (kills.length >= 1 && p.kills === kills[0] && p.kills > 0) { r.points += 1; r.firsts++; }
          else if (kills.length >= 2 && p.kills === kills[1] && p.kills > 0) { r.points += 0.5; r.seconds++; }
        }
      }
      if (live) setRows({ rows: [...board.values()].sort((a, b) => b.points - a.points || b.firsts - a.firsts || b.kills - a.kills), matches: week.length });
    })().catch((e) => live && setError(String(e?.message ?? e)));
    return () => { live = false; };
  }, [who, range]);
  const medals = ['#E5C76B', '#C8D0DB', '#CD8B55'];
  return (
    <>
      <div className="g-chips">
        <Chip on={back === 0} onClick={() => setBack(0)}>This week</Chip>
        <Chip on={back === 1} onClick={() => setBack(1)}>Last week</Chip>
      </div>
      {error ? <Asking text={`Could not build the totals: ${error}`} /> : !rows ? <Asking text={progress} /> : (
        <>
          <h3 className="t-h">Private matches   {fmt(range.start, DAY_TIME)} to {fmt(range.end, DAY_TIME)}</h3>
          <p className="t-status">{rows.matches} matches this week   |   most kills in a match = 1 point, second most = 0.5 point   |   week resets Tuesday</p>
          {rows.rows.length === 0 && <p className="t-empty">No private matches in this week yet.</p>}
          {rows.rows.map((r, i) => {
            const color = i < 3 && r.points > 0 ? medals[i] : '#7FA6D6';
            const max = Math.max(1, rows.rows[0].points);
            return (
              <div key={r.memberId} className={`t-total ${i === 0 && r.points > 0 ? 'lead' : ''}`}>
                <b className="n" style={{ color }}>{i + 1}</b>
                <span className="who"><b style={{ color: r.memberId === who.membershipId ? 'var(--accent-text)' : undefined }}>{r.short}</b><span>{r.firsts}x 1st   {r.seconds}x 2nd   {r.matches} played   {r.kills} kills</span></span>
                <span className="track"><span style={{ width: `${(100 * r.points) / max}%`, background: color }} /></span>
                <b className="pts" style={{ color }}>{Math.round(r.points * 10) / 10} pts</b>
              </div>
            );
          })}
        </>
      )}
    </>
  );
}

// ---------------- match report ----------------

const fullReports = new Map<string, Promise<any>>();

function Report({ id, me, back }: { id: string; me: string; back: () => void }) {
  const [d, setD] = useState<any>();
  const [names, setNames] = useState<{ acts: Record<string, any>; lite: Record<string, any> }>();
  const [error, setError] = useState('');
  const top = useRef<HTMLDivElement>(null);
  useEffect(() => {
    top.current?.scrollIntoView({ block: 'nearest' });
    let p = fullReports.get(id);
    if (!p) { p = bungie<any>(`/Destiny2/Stats/PostGameCarnageReport/${id}/`, { stats: true }); fullReports.set(id, p); p.catch(() => fullReports.delete(id)); }
    p.then(setD).catch((e) => setError(String(e?.message ?? e)));
    void Promise.all([table<any>('DestinyActivityDefinition'), items()]).then(([acts, lite]) => setNames({ acts, lite }));
  }, [id]);
  return (
    <div className="t-page" ref={top}>
      <div className="t-top"><SmallButton onClick={back}>Back to matches</SmallButton></div>
      {error ? <Asking text={`Could not load this match: ${error}`} /> : !d || !names ? <Asking text="Loading the match report from Bungie..." /> : <ReportBody d={d} names={names} me={me} />}
    </div>
  );
}

function ReportBody({ d, names, me }: { d: any; names: { acts: Record<string, any>; lite: Record<string, any> }; me: string }) {
  const det = d.activityDetails ?? {};
  const act = names.acts[det.referenceId];
  const map = act?.displayProperties?.name ?? '';
  const mode = names.acts[det.directorActivityHash]?.displayProperties?.name ?? '';
  const image = act?.pgcrImage;
  const teams: { id: string; name: string; color: string; standing: string; score: number }[] = (d.teams ?? []).map((t: any, i: number) => ({ id: String(t.teamId), name: t.teamName && !/^\d+$/.test(t.teamName) ? t.teamName : `Team ${i + 1}`, color: TEAM_BY_NAME[t.teamName] ?? TEAM_COLORS[i % 4], standing: t.standing?.basic?.displayValue ?? '', score: t.score?.basic?.value ?? 0 }));
  const players = (d.entries ?? []).map((e: any) => {
    const u = e.player?.destinyUserInfo, v = e.values, x = e.extended?.values;
    return {
      name: fullName(u), short: shortName(u), memberId: u?.membershipId ?? '', icon: u?.iconPath, cls: e.player?.characterClass ?? '', light: e.player?.lightLevel ?? 0,
      team: String(v?.team?.basic?.value ?? ''), kills: val(v, 'kills'), deaths: val(v, 'deaths'), assists: val(v, 'assists'), kd: val(v, 'killsDeathsRatio'), eff: val(v, 'efficiency'),
      score: val(v, 'score'), seconds: val(v, 'timePlayedSeconds'), completed: val(v, 'completed'), precision: val(x, 'precisionKills'), sup: val(x, 'weaponKillsSuper'),
      grenade: val(x, 'weaponKillsGrenade'), melee: val(x, 'weaponKillsMelee'), ability: val(x, 'weaponKillsAbility'),
      weapons: (e.extended?.weapons ?? []).map((w: any) => ({ name: names.lite[w.referenceId]?.name ?? '', kills: val(w.values, 'uniqueWeaponKills'), precision: val(w.values, 'uniqueWeaponPrecisionKills') }))
        .sort((a: any, b: any) => b.kills - a.kills),
    };
  });
  const seconds = Math.max(0, ...players.map((p: any) => p.seconds));
  const groups: [typeof teams[number] | null, any[]][] = teams.length ? teams.map((t) => [t, players.filter((p: any) => p.team === t.id)]) : [[null, players]];
  return (
    <>
      <div className="t-hero" style={image ? { backgroundImage: `linear-gradient(90deg, rgba(10,10,14,0.85), rgba(10,10,14,0.35)), url(${img(image)})` } : undefined}>
        <div>
          <h2>{mode || 'Crucible'}  -  {map}</h2>
          <p>{fmt(new Date(d.period), { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}   |   {Math.floor(seconds / 60)}m {String(Math.floor(seconds % 60)).padStart(2, '0')}s{det.isPrivate ? '   |   private match' : ''}</p>
        </div>
        <div className="scores">
          {teams.map((t) => <span key={t.id} style={{ background: t.color }}><b>{t.name.toUpperCase()}   {t.score}</b><span>{t.standing}</span></span>)}
        </div>
      </div>
      {groups.map(([team, list], gi) => {
        const color = team?.color ?? '#8a8f99';
        return (
          <div key={gi} className="t-team">
            <div className="t-prow head">
              <b style={{ color }}>{team ? `${team.name}   ${team.standing}   score ${team.score}` : 'Players'}</b>
              {['Kills', 'Deaths', 'Assists', 'K/D', 'Eff', 'Score'].map((x) => <span key={x}>{x}</span>)}
            </div>
            {[...list].sort((a, b) => b.score - a.score || b.kills - a.kills).map((p) => <PlayerRow key={p.memberId + p.cls} p={p} color={color} me={p.memberId === me} />)}
          </div>
        );
      })}
    </>
  );
}

function PlayerRow({ p, color, me }: { p: any; color: string; me: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <button type="button" className={`t-player ${me ? 'me' : ''}`} style={{ borderLeftColor: color, background: `color-mix(in srgb, ${color} ${me ? 25 : 15}%, transparent)` }}
      onClick={() => setOpen(!open)} title={`${p.name}\nClick to show weapons and ability kills.`}>
      <span className="t-prow">
        <span className="who">
          {p.icon ? <img src={img(p.icon)} alt="" width={34} height={34} /> : <span style={{ width: 34 }} />}
          <span><b>{p.short}</b><span>{p.cls}   {p.light}{p.completed === 0 ? '   (left early)' : ''}</span></span>
        </span>
        <span>{p.kills}</span><span>{p.deaths}</span><span>{p.assists}</span>
        <b className={p.kd >= 1 ? 'good' : ''}>{p.kd.toFixed(2)}</b><span>{p.eff.toFixed(2)}</span><span>{p.score}</span>
      </span>
      {open && (
        <span className="more">
          {p.weapons.map((w: any, i: number) => <span key={i}>{w.name || 'Unknown weapon'}   {w.kills} kills{w.precision > 0 ? `  (${w.precision} precision)` : ''}</span>)}
          <span className="dim">Super {p.sup}   Grenade {p.grenade}   Melee {p.melee}   Abilities {p.ability}   Precision {p.precision}</span>
        </span>
      )}
    </button>
  );
}
